import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { postUploadSponsor } from "../../../../../apis";
import Check from "../../../../../assets/icons/Check";
import CloseIcon from "../../../../../assets/icons/CloseIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import UploadIcon from "../../../../../assets/icons/UploadIcon";
import { Input, InputText } from "../../../../../components/common/Input";
import Select from "../../../../../components/common/Select";
import { MAX_UPLOAD_IMAGE_SIZE } from "../../../../../constants";
import useOutsideClick from "../../../../../hooks/useOutsideClick";

const POSITION = ["Tier 1", "Tier 2", "Tier 3"];

const CreateSponsor = ({
  onSave,
  onRemove,
  id,
  sponsor = "",
  position = "",
  imageUrl = "",
  SPONSORS_TITLE,
  onCancel = () => {},
  websiteLink,
  openUploadPopUp,
}) => {
  const { eventId } = useParams();

  const [showSponsorsDropdown, setShowSponsorsDropdown] = useState(false);
  const [customSponsorName, setCustomSponsorName] = useState("");
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [positionValue, setPositionValue] = useState(position);
  const [sponsorValue, setSponsorValue] = useState(sponsor);
  const [image, setImage] = useState(imageUrl);
  const [saveButtonDisable, setSaveButtonDisable] = useState(true);
  const [uploadLoader, setUploadLoader] = useState(false);
  const [webLink, setWebLink] = useState(websiteLink);

  const sponsorDropdownRef = useRef();
  const positionDropdownRef = useRef();
  const oldValuesRef = useRef({
    position,
    imageUrl,
    sponsor,
    webLink,
  });
  const fileUploadRef = useRef();

  const toggleSponsorDropdown = () => setShowSponsorsDropdown((prev) => !prev);
  const togglePositionDropdown = () => setShowPositionDropdown((prev) => !prev);

  useOutsideClick(positionDropdownRef, () => setShowPositionDropdown(false));
  useOutsideClick(sponsorDropdownRef, () => setShowSponsorsDropdown(false));

  const uploadFile = async (e) => {
    if (!uploadLoader) {
      if (
        e.target.files[0].size &&
        e.target.files[0].size > MAX_UPLOAD_IMAGE_SIZE
      ) {
        window.Toast.fire({
          icon: "error",
          title: "Image size is more than 6 MB",
        });
        return;
      }
      setUploadLoader(true);
      try {
        const data = await postUploadSponsor(e.target.files[0], eventId);
        setImage(data);
      } catch (error) {
        console.error(error);
        window.Toast.fire({
          icon: "error",
          title: "Something went wrong",
        });
      } finally {
        setUploadLoader(false);
      }
    }
  };

  useEffect(() => {
    if (
      sponsorValue !== sponsor ||
      position !== positionValue ||
      image !== imageUrl ||
      webLink !== websiteLink
    )
      setSaveButtonDisable(false);
    else setSaveButtonDisable(true);
  }, [sponsorValue, positionValue, image, webLink]);

  useEffect(() => {
    if (openUploadPopUp) fileUploadRef.current.click();
  }, [openUploadPopUp]);

  return (
    <div className="flex items-center gap-12">
      <label>
        <input
          ref={fileUploadRef}
          onChange={(e) => uploadFile(e)}
          className="hidden"
          type="file"
          accept=".png"
        />

        <div
          title="Upload image"
          className={` font-medium mx-auto cursor-pointer !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-1.5 `}
        >
          {uploadLoader ? (
            <div className="clipPathLoader mx-auto"></div>
          ) : (
            <UploadIcon />
          )}
        </div>
      </label>
      {image ? (
        <img
          src={"https://img2.pixnif.com/" + image}
          height={40}
          width={40}
          className="object-contain hover:scale-[5] transition-transform"
          alt="Sponsor image"
        />
      ) : (
        <div className="w-[40px]"></div>
      )}

      <div ref={sponsorDropdownRef} className="relative">
        <Select
          onClick={toggleSponsorDropdown}
          text={sponsorValue ? sponsorValue : "Select sponsor type"}
          isOpen={showSponsorsDropdown}
          className="w-[200px]"
        />
        <div className="absolute w-full rounded-[10px] overflow-hidden bg-dark-gray shadow-card z-40 right-15 mt-3 top-[99%]">
          <div className="flex select-none flex-col text-light-gray text-sm">
            {showSponsorsDropdown && (
              <>
                <div className="h-[130px] w-full overflow-auto">
                  {SPONSORS_TITLE.map((el) => (
                    <button
                      key={el}
                      className="flex w-full relative px-4 py-3 hover:bg-black hover:text-primary-green"
                      onClick={() => {
                        setSponsorValue(el);
                        toggleSponsorDropdown();
                      }}
                    >
                      <span className="capitalize truncate">{el}</span>
                    </button>
                  ))}
                </div>
                <div className="border-t border-secondary-600 px-10 pt-1 h-1"></div>
                <div className="px-2">
                  <InputText
                    onChange={(e) => {
                      setCustomSponsorName(e);
                    }}
                    value={customSponsorName}
                    placeholder="Sponsor Name"
                    className="mb-3"
                    postFix
                    postFixComponent={
                      <button
                        onClick={() => {
                          setSponsorValue(customSponsorName);
                          setCustomSponsorName("");
                          toggleSponsorDropdown();
                        }}
                      >
                        {" "}
                        <Check />{" "}
                      </button>
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div ref={positionDropdownRef} className="relative">
        <Select
          onClick={togglePositionDropdown}
          text={positionValue ? positionValue : "Position"}
          isOpen={showPositionDropdown}
          className="w-[120px]"
        />
        {showPositionDropdown && (
          <div className="absolute w-full rounded-[10px] overflow-hidden bg-dark-gray shadow-card z-40 right-15 mt-3 top-[99%]">
            <div className="flex select-none flex-col text-light-gray text-sm">
              <div className="h-[130px] w-full overflow-auto">
                {POSITION.map((el) => (
                  <button
                    key={el}
                    className="flex w-full relative px-4 py-3 hover:bg-black hover:text-primary-green"
                    onClick={() => {
                      setPositionValue(el);
                      togglePositionDropdown();
                    }}
                  >
                    <span className="capitalize truncate">{el}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Input
        value={webLink}
        onChange={setWebLink}
        className="md:w-[300px]"
        placeholder="https://www.algomage.com"
      />

      <div className="flex items-center gap-4">
        <button
          className={`font-medium  text-center !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-1.5 w-[40px] h-[32px] ${
            saveButtonDisable
              ? "cursor-not-allowed opacity-75"
              : "cursor-pointer"
          }`}
          onClick={async () => {
            setSaveButtonDisable(true);
            const dataPushed = await onSave(
              id,
              sponsorValue,
              positionValue,
              image,
              webLink
            );
            setSaveButtonDisable(dataPushed);
            if (!dataPushed) {
              if (id) {
                setPositionValue(oldValuesRef.current.position);
                setImage(oldValuesRef.current.imageUrl);
                setSponsorValue(oldValuesRef.current.sponsor);
                setWebLink(oldValuesRef.current.webLink);
              }
            } else {
              oldValuesRef.current = {
                sponsor: sponsorValue,
                imageUrl: image,
                position: positionValue,
                webLink: webLink,
              };
            }
          }}
          disabled={saveButtonDisable}
        >
          <Check />
        </button>
        {id ? (
          <button
            className={`font-medium cursor-pointer !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-1.5 w-[40px] h-[32px]`}
            onClick={() => onRemove(id)}
          >
            <DeleteIcon />
          </button>
        ) : (
          <button
            className={`font-medium cursor-pointer !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-1.5 w-[40px] h-[32px] text-center mx-auto justify-center`}
            onClick={onCancel}
          >
            X
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateSponsor;
